import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { getFundsPositions } from "../../../../../../connectors/operationApi";
import { getInvestmentsFundsNoOperableByID } from "../../../../../../connectors/marketApi";
import {
  InvestmentFundsOperable,
  UserAccount,
} from "../../../../../../types/types";

const NON_OPERABLE_FUND_IDS: {
  fund_id: number;
  bloomberg_code: string;
}[] = [
  { fund_id: 4, bloomberg_code: "ALRVIIA:AR" },
  { fund_id: 36, bloomberg_code: "aldiiia ar equity" },
];

const pickClassesForAccount = (
  classes: InvestmentFundsOperable[],
  account: UserAccount | null,
): InvestmentFundsOperable[] => {
  if (!account) return classes.slice(0, 1);
  if (Math.floor(Number(account.number) / 1_000_000) === 24)
    return classes.filter((c) => c.name.includes("Clase Blanqueo"));
  if (account.isJuridical)
    return classes.filter(
      (c) =>
        (c.name.includes("Clase B") || c.name.includes("Clase C")) &&
        !c.name.includes("Clase Blanqueo"),
    );
  return classes.filter((c) => c.name.includes("Clase A"));
};

export const useNonOperableFunds = ({
  accountSelected,
  operableFunds,
}: {
  accountSelected: UserAccount | null;
  operableFunds: InvestmentFundsOperable[];
  dateFilter?: Date | string;
}) => {
  // Only query positions/details for funds that are NOT already in the operable list
  const missingFromOperable = useMemo(() => {
    if (operableFunds.length === 0) return [];
    const operableBloombergCodes = new Set(
      operableFunds.map((f) => f.bloomberg_code.toLowerCase()),
    );
    return NON_OPERABLE_FUND_IDS.filter(
      (nof) => !operableBloombergCodes.has(nof.bloomberg_code.toLowerCase()),
    );
  }, [operableFunds]);

  const { data: positions } = useQuery({
    queryKey: ["funds-positions", accountSelected?.number],
    queryFn: () =>
      getFundsPositions({ accountId: String(accountSelected?.number) }),
    enabled: !!accountSelected?.number && missingFromOperable.length > 0,
    staleTime: Infinity,
  });

  const { nonOperableMatches, nonOperablePositions } = useMemo(() => {
    if (!positions || missingFromOperable.length === 0)
      return {
        nonOperableMatches: [],
        nonOperablePositions: new Map<string, number>(),
      };

    const uniqueMatches = new Map<number, (typeof NON_OPERABLE_FUND_IDS)[0]>();
    const positionsByBloomberg = new Map<string, number>();

    for (const pos of positions.esco_positions) {
      const match = missingFromOperable.find(
        (nof) =>
          nof.bloomberg_code.toLowerCase() ===
          pos.bloomberg_interface_code.toLowerCase(),
      );
      if (match && pos?.quantity > 0) {
        if (!uniqueMatches.has(match.fund_id))
          uniqueMatches.set(match.fund_id, match);
        positionsByBloomberg.set(
          pos.bloomberg_interface_code.toLowerCase(),
          pos.quantity,
        );
      }
    }

    return {
      nonOperableMatches: Array.from(uniqueMatches.values()),
      nonOperablePositions: positionsByBloomberg,
    };
  }, [positions, missingFromOperable]);

  const fundQueries = useQueries({
    queries: nonOperableMatches.map((match) => ({
      queryKey: ["investment-fund-no-operable", match.fund_id],
      queryFn: () =>
        getInvestmentsFundsNoOperableByID({
          id: match.fund_id,
        }),
      staleTime: Infinity,
    })),
  });

  const nonOperableFunds = useMemo(() => {
    return fundQueries
      .filter((q) => q.isSuccess && q.data)
      .flatMap((q) =>
        pickClassesForAccount(q.data!.classes, accountSelected).map(
          (fc) => ({ ...fc, nonOperable: true }) as InvestmentFundsOperable,
        ),
      );
  }, [fundQueries, accountSelected]);

  const isWaitingForPositions =
    !!accountSelected?.number && missingFromOperable.length > 0 && !positions;

  const allFundQueriesSettled =
    nonOperableMatches.length === 0 ||
    (fundQueries.length === nonOperableMatches.length &&
      fundQueries.every((q) => q.isSuccess || q.isError));

  const isLoadingNonOperable = isWaitingForPositions || !allFundQueriesSettled;

  return { nonOperableFunds, isLoadingNonOperable, nonOperablePositions };
};
